/**
 * Trajets de tout le monde (section Trajets, personnel) —
 * GET /api/admin/rentals?q=&type=&from=&to=&limit=&offset=
 *
 * Chaque ligne porte déjà son véhicule, son locataire et son propriétaire.
 * `q` : nom, prénom, e-mail, téléphone, marque, modèle, numéro de série,
 * numéro de trajet (#xxxxxxxx) ; plusieurs mots se combinent.
 */

export interface AdminRentalPerson {
  id_user: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  profile_photo?: string | null;
  average_rating?: number | string | null;
  license_verified?: boolean;
}

export interface AdminRentalRow {
  id_rental: string;
  vehicle_id: string;
  renter_id: string;
  owner_id: string;
  status: string;
  start_date: string;
  end_date: string | null;
  total_price: number | string | null;
  vehicle_type?: string | null;
  [key: string]: any;
  vehicle: {
    id_vehicle: string | null; brand: string | null; model: string | null; vehicle_type: string | null;
    serial_number: string | null; year: number | null; hourly_rate: number | null; daily_rate: number | null;
    is_available: boolean | null; is_suspended: boolean;
    latitude: number | null; longitude: number | null; return_latitude: number | null; return_longitude: number | null;
    thumb_url?: string | null;
  };
  renter: AdminRentalPerson;
  owner: AdminRentalPerson;
}

export interface AdminRentalSearch {
  q?: string;
  type?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export const searchAdminRentals = async (
  token: string,
  search: AdminRentalSearch,
  signal?: AbortSignal
): Promise<{ total: number; limit: number; offset: number; rows: AdminRentalRow[] }> => {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') params.set(key, String(value));
  });
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/admin/rentals?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data.data;
};

export const personName = (p?: AdminRentalPerson | null) =>
  p ? [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.email || '' : '';

/** La location qui compte pour chaque véhicule (liste de la Flotte). */
export type VehicleRentalState = 'ongoing' | 'accepted' | 'pending' | 'completed' | 'cancelled';
export interface VehicleRentalStatus {
  vehicle_id: string;
  id_rental: string;
  state: VehicleRentalState;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  updated_at: string | null;
  renter_name: string | null;
}

export const getRentalStateByVehicle = async (token: string): Promise<VehicleRentalStatus[]> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/admin/rentals/by-vehicle`, { headers: { Authorization: `Bearer ${token}` } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data.data as VehicleRentalStatus[];
};
